import { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface WebhookSession {
  sessionId: string;
  status: 'waiting' | 'authenticated' | 'error';
  user?: any;
}

export const useDiscourseWebhook = () => {
  const [webhookSession, setWebhookSession] = useState<WebhookSession | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Register popup session with backend
  const registerPopupSession = useCallback(async (email?: string, username?: string): Promise<string> => {
    try {
      const sessionId = Math.random().toString(36).substring(7);
      console.log(">>>>>>>>")
      console.log(BACKEND_URL)
      const response = await fetch(`${BACKEND_URL}/api/webhooks/register-popup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          email,
          username,
        }),
      });
      
      const data = await response.json();
      
      if (data.success) {
        setWebhookSession({
          sessionId,
          status: 'waiting',
        });
        return sessionId;
      }
      
      throw new Error('Failed to register popup session');
    } catch (error) {
      console.error('Error registering popup:', error);
      throw error;
    }
  }, []);
  
  // Start polling for webhook updates
  const startPolling = useCallback((sessionId: string, onSuccess: (user: any) => void) => {
    if (isPolling) return;
    
    setIsPolling(true);
    let pollCount = 0;
    const maxPolls = 60; // Poll for max 2 minutes (60 * 2 seconds)
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/webhooks/check-popup/${sessionId}`);
        const data = await response.json();
        
        console.log(`🔄 Poll ${pollCount}: ${data.status}`);
        
        if (data.status === 'authenticated' && data.user) {
          // Success! User logged in via webhook
          console.log('✅ Webhook authentication successful!');
          
          clearInterval(pollInterval);
          setIsPolling(false);
          
          setWebhookSession({
            sessionId: data.sessionId,
            status: 'authenticated',
            user: data.user,
          });
          
          // Store session with correct key
          localStorage.setItem('auth_session_id', data.sessionId);
          
          // Call success callback
          onSuccess(data.user);
          
        } else if (data.status === 'not_found' || pollCount >= maxPolls) {
          // Session not found or timeout
          clearInterval(pollInterval);
          setIsPolling(false);
          
          setWebhookSession({
            sessionId,
            status: 'error',
          });
        }
      } catch (error) {
        console.error('Polling error:', error);
        
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setIsPolling(false);
          
          setWebhookSession({
            sessionId,
            status: 'error',
          });
        }
      }
    }, 2000); // Poll every 2 seconds
    
    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [isPolling]);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      setIsPolling(false);
    };
  }, []);
  
  return {
    registerPopupSession,
    startPolling,
    webhookSession,
    isPolling,
  };
};